/// <amd-module name="Repos/Changes/Components/ChangeFileContent" />
import * as React from "react";
import { IChangeItemContent } from "Repos/ChangesCommon/Contracts";
import { IEditorSelection, IFileContentDetails, IFileViewerProps } from "Repos/Common/Contracts/Editor";
import { IReadonlyObservableArray, IReadonlyObservableValue } from "VSS/Core/Observable";
import { IEditorWidgetRegistration, IFilesHubViewService } from "VSS/Features/Monaco/Contracts";
import { PerformanceScenarioContext } from "VSS/Platform/Components/Performance";
import { css, IVssComponentContext, usePageContext, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { Card } from "VSSUI/Card";
import { Observer } from "VSSUI/Observer";
import { FileDiscussionWidget } from "./Discussion/FileDiscussionWidget";
import { HistoryBehavior, IPageStateHistoryService } from "VSS/Features/History";
import { getSelectionFromQueryParams } from "../Util";

export interface IChangeFileContentProps {
    /**
     * List of custom widget registrations for the file content viewer which uses Monaco editor.
     */
    diffWidgets?: IEditorWidgetRegistration[] | IReadonlyObservableArray<IEditorWidgetRegistration>;

    /**
     * Url to download the specified file at the specified version
     *
     * @param path Full path of the file
     * @param version Version at which to get the file
     */
    fileDownloadUrl: string;

    /**
     * Content information for the specified file
     *
     * @param path File path
     * @param version version of the file
     */
    itemContent: IReadonlyObservableValue<IChangeItemContent | undefined>;

    /**
     * If set, scroll to the specified line number.
     */
    scrollToLine?: IReadonlyObservableValue<number | undefined>;
}

export class ChangeFileContent extends VssComponent<IChangeFileContentProps> {
    constructor(props: IChangeFileContentProps, context: IVssComponentContext) {
        super(props, context);
    }
    
    public render() {
        const { diffWidgets, fileDownloadUrl, itemContent, scrollToLine } = this.props;
        const selection = getSelectionFromQueryParams(this.context.pageContext) as IEditorSelection;

        return (
            <PerformanceScenarioContext.Consumer>
                {scenarioProps => {
                    const pendingWorkComplete = scenarioProps.registerPendingWork("load-file-editor")!;

                    return (
                        <Observer content={itemContent}>
                            {(contentProps: { content: IChangeItemContent | undefined }) => {
                                let contentDetails: IFileContentDetails | undefined;
                                if (contentProps.content) {
                                    contentDetails = contentProps.content.details;
                                }

                                let fileDiscussionElement: JSX.Element | null = null;
                                const nonBinaryContent = contentDetails && !contentDetails.metadata.isBinary;
                                if (contentDetails && !nonBinaryContent) {
                                    fileDiscussionElement = <FileDiscussionWidget className="margin-left-16 margin-top-16" widgets={diffWidgets} />;
                                }

                                return (
                                    <Card
                                        className={css("no-padding", nonBinaryContent && "flex-grow v-scroll-hidden")}
                                        contentProps={{ className: "relative", contentPadding: false }}
                                    >
                                        {contentDetails ? (
                                            <div className={css("flex-column scroll-hidden flex-grow", nonBinaryContent && "absolute-fill")}>
                                                {fileDiscussionElement}
                                                <WrappedComponent<IFileViewerProps>
                                                    dependencies={["ms.vss-code-web.code-editor"]}
                                                    wrappedType="repos-file-viewer"
                                                    wrappedProps={{
                                                        className: "flex-row flex-grow",
                                                        content: contentDetails,
                                                        editorWidgets: diffWidgets,
                                                        editorClassName: "repos-commit-editor-background scroll-hidden",
                                                        getDownloadUrl: () => fileDownloadUrl,
                                                        onLoaded: pendingWorkComplete && pendingWorkComplete.complete,
                                                        scrollToLine: scrollToLine,
                                                        showMarginForIcons: Boolean(diffWidgets),
                                                        initialSelectedText: selection
                                                    }}
                                                />
                                            </div>
                                        ) : (
                                            <div className="repos-commit-editor-background flex-grow" />
                                        )}
                                    </Card>
                                );
                            }}
                        </Observer>
                    );
                }}
            </PerformanceScenarioContext.Consumer>
        );
    }
}
